import React from "react";
import { Paper, Box, Typography, Button, alpha, useTheme } from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";

interface EmptyReportsStateProps {
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

export const EmptyReportsState: React.FC<EmptyReportsStateProps> = ({
  hasActiveFilters,
  onResetFilters,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Paper
      sx={{
        p: { xs: 4, md: 6 },
        textAlign: "center",
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: 3,
        border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
      }}
      elevation={0}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
        }}
      >
        <FolderOpenIcon
          sx={{
            fontSize: 50,
            color: alpha(theme.palette.primary.main, 0.6),
          }}
        />
      </Box>
      <Typography
        variant="h5"
        fontWeight={600}
        color="text.primary"
        gutterBottom
      >
        {hasActiveFilters ? "No matching reports found" : "No reports yet"}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 450, mx: "auto", mb: 4 }}
      >
        {hasActiveFilters
          ? "Try adjusting your filters or search criteria to find what you're looking for."
          : "Get started by creating your first incident report."}
      </Typography>
      {hasActiveFilters ? (
        <Button
          variant="outlined"
          onClick={onResetFilters}
          startIcon={<CloseIcon />}
          sx={{ 
            textTransform: "none",
            px: 3,
            py: 1,
          }}
        >
          Clear all filters
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={() => navigate("/video-selection")}
          startIcon={<AddIcon />}
          size="large"
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.2,
            fontSize: "1rem",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 4px 14px 0 ${alpha(
              theme.palette.primary.main,
              0.4
            )}`,
            "&:hover": {
              boxShadow: `0 6px 20px 0 ${alpha(
                theme.palette.primary.main,
                0.5
              )}`,
            },
          }}
        >
          Create New Report
        </Button>
      )}
    </Paper>
  );
};
